import React, { useState, useEffect } from 'react';
import { 
  Typography, Grid, Box, 
  LinearProgress,
  Stack, Card, CardContent, useTheme, alpha,
  Button, Container
} from '@mui/material';
import { 
  Science as ScienceIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import TestRecords from './tests-tab-components/TestRecords';
import CertificateOfAnalysis from './tests-tab-components/CertificateOfAnalysis';

const TestsTab = ({ formulation, showSnackbar, user, onUpdate }) => {
  const theme = useTheme();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel, canApproveFormulations } = usePermissions();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [testsCompleteLoading, setTestsCompleteLoading] = useState(false);

  // Check permissions for tests tab specifically
  const canViewTests = hasPermission('products.formulations.tests.view', 'read');
  const canEditTests = hasPermission('products.formulations.tests.edit', 'write');
  const canDeleteTests = hasPermission('products.formulations.tests.delete', 'delete');
  const canApprove = canApproveFormulations();
  
  // Fetch customers from the database
  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('name');
        
      if (error) throw error;
      
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      showSnackbar('Error fetching customers', 'error');
    }
  };
  
  // Fetch brands from the database
  const fetchBrands = async () => {
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .order('name');
        
      if (error) throw error;
      
      setBrands(data || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
      showSnackbar('Error fetching brands', 'error');
    }
  };
  
  // Fetch products from the database
  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('name');
        
      if (error) throw error;
      
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      showSnackbar('Error fetching products', 'error');
    }
  };

  const fetchTests = async () => {
    setLoading(true);
    try {      
      const { data, error } = await supabase
        .from('formulation_tests')
        .select('*')
        .eq('formulation_id', formulation.id)
        .order('test_date', { ascending: false });
        
      if (error) throw error;
      
      setTests(data || []);
    } catch (error) {
      console.error('Error fetching tests:', error);
      showSnackbar('Error fetching test records', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load tests for the current formulation
  useEffect(() => {
    if (formulation?.id) {
      fetchTests();
      fetchCustomers();
      fetchBrands();
      fetchProducts();
    }
  }, [formulation?.id]);

  const handleMarkTestsComplete = async () => {
    if (!formulation?.id || !user?.id) return;
    
    setTestsCompleteLoading(true);
    try {
      const newCompleteStatus = !formulation.tests_complete;
      
      const { error } = await supabase
        .from('formulations')
        .update({
          tests_complete: newCompleteStatus,
          tests_completed_at: newCompleteStatus ? new Date().toISOString() : null,
          tests_completed_by: newCompleteStatus ? user.id : null
        })
        .eq('id', formulation.id);
        
      if (error) throw error;
      
      showSnackbar(
        newCompleteStatus ? 'Testing marked as complete!' : 'Testing marked as incomplete', 
        'success'
      );
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating tests completion:', error);
      showSnackbar('Error updating testing status: ' + error.message, 'error');
    } finally {
      setTestsCompleteLoading(false);
    }
  };

  // Permission check
  if (!canViewTests) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 6,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 500,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
            zIndex: 0,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <ScienceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 3,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Formulation Tests Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have the necessary permissions to access the Formulation Tests. Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Enhanced Header Section */}
      <Card 
        sx={{ 
          borderRadius: 3,
          mb: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <ScienceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                    Testing & Quality Control
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Manage test records and certificates of analysis for your formulation
                  </Typography>
                </Box>
              </Stack>
              
              {/* Quick Stats */}
              <Stack direction="row" spacing={3} sx={{ mt: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {tests.filter(test => test.test_type !== 'certificate_of_analysis').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Test Records
                  </Typography>
                </Box>
                <Box sx={{ borderRight: `1px solid ${theme.palette.divider}`, pr: 3 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {tests.filter(test => test.test_type === 'certificate_of_analysis').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Certificates
                  </Typography>
                </Box>
                <Box sx={{ borderRight: `1px solid ${theme.palette.divider}`, pr: 3 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 500, 
                    color: tests.filter(test => test.status === 'passed' || test.is_passed === true).length > 0 ? 'success.main' : 'text.secondary'
                  }}>
                    {tests.filter(test => test.status === 'passed' || test.is_passed === true).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Passed
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            {(canEditTests || canApprove) && (
              <Box>
                <Button
                  variant={formulation?.tests_complete ? "contained" : "outlined"}
                  startIcon={formulation?.tests_complete ? <CheckCircleIcon /> : <CheckIcon />}
                  onClick={handleMarkTestsComplete}
                  disabled={testsCompleteLoading}
                  sx={{ 
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    background: formulation?.tests_complete 
                      ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                      : 'white',
                    border: formulation?.tests_complete 
                      ? 'none'
                      : `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    color: formulation?.tests_complete ? 'white' : theme.palette.success.main,
                    fontWeight: 500,
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.15)}`,
                    '&:hover': {
                      background: formulation?.tests_complete 
                        ? `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`
                        : alpha(theme.palette.success.main, 0.08),
                      borderColor: formulation?.tests_complete ? 'none' : theme.palette.success.main,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.25)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {testsCompleteLoading ? 'Updating...' : 
                   formulation?.tests_complete ? 'Mark Incomplete' : 'Mark Complete'}
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            
      {/* Test Records Component */}
      <TestRecords 
        formulation={formulation}
        showSnackbar={showSnackbar}
        user={user}
        tests={tests}
        setTests={setTests}
        loading={loading}
        setLoading={setLoading}
        fetchTests={fetchTests}
        canEdit={canEditTests}
        canDelete={canDeleteTests}
      />

      {/* Certificate of Analysis Component */}
      <CertificateOfAnalysis 
        formulation={formulation}
        showSnackbar={showSnackbar}
        user={user}
        tests={tests}
        setTests={setTests}
        loading={loading}
        setLoading={setLoading}
        fetchTests={fetchTests}
        customers={customers}
        brands={brands}
        products={products}
        canEdit={canEditTests}
        canDelete={canDeleteTests}
      />
    </Box>
  );
};

export default TestsTab;